import { LabelChip } from "@carbon6/c6-frontend-design";
import type { StatusTokens } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { sessionSimpleStatus, type SessionStatusKey } from "@/modules/training/public";
import { statusChipColor, useStatusTokens } from "./statusTokens";

// 앱의 표시 카테고리를 c6 status.job 어휘에 연결하는 유일한 표. `unknown` 만 도메인 밖
// 신호다. 그래서 `unknown` 은 코어 muted 를 직접 쓴다(c6 RFC-status-token-layering §그룹 선택 가이드).
//
// `cancel_requested` 는 카테고리 `in_progress` 로 합친다. 그래서 c6 `job.cancelling` 은 쓰지 않는다.
// SessionStatusKey 는 칩 색이면서 서버 필터 어휘(`status_group`)이기도 하다. 카테고리를
// 늘리면 백엔드 계약도 함께 바뀐다.
const JOB_TOKEN: Record<SessionStatusKey, (status: StatusTokens) => string> = {
  waiting: (status) => status.job.pending,
  in_progress: (status) => status.job.running,
  succeeded: (status) => status.job.succeeded,
  failed: (status) => status.job.failed,
  action_required: (status) => status.job.actionRequired,
  cancelled: (status) => status.job.cancelled,
  unknown: (status) => status.muted,
};

export type SessionStatusChipProps = {
  // 서버가 보낸 raw 상태 문자열. 카테고리 합치기와 라벨은 sessionSimpleStatus 가 소유한다.
  status: string;
  size?: "xs" | "sm" | "md";
  variant?: "filled" | "outline";
  showIcon?: boolean;
  // 기본은 카테고리 라벨이다. 개수나 세부 라벨을 표시하는 표면만 직접 채운다.
  children?: ReactNode;
};

// Training 세션·Evaluation run 의 상태 칩. 이 컴포넌트 하나가 카테고리 라벨과 색을 소유한다.
// 그래서 색만 다른 두 번째 표가 생기지 않는다.
export function SessionStatusChip({
  status,
  size = "xs",
  variant = "filled",
  showIcon = true,
  children,
}: SessionStatusChipProps): ReactNode {
  const statusTokens = useStatusTokens();
  const simple = sessionSimpleStatus(status);
  return (
    <LabelChip
      color={statusChipColor(JOB_TOKEN[simple.key](statusTokens))}
      size={size}
      variant={variant}
      showIcon={showIcon}
    >
      {children ?? simple.label}
    </LabelChip>
  );
}
